import { Fragment } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  return (
    <nav className="flex items-center justify-center space-x-1" aria-label="Paginação">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-2 text-sm font-medium border rounded-md hover:bg-opacity-80 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{
          color: 'var(--text-secondary)',
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-color)'
        }}
        aria-label="Página anterior"
      >
        Anterior
      </button>

      {visiblePages.map((page, index) => (
        <Fragment key={index}>
          {page === '...' ? (
            <span className="px-3 py-2 text-sm font-normal" style={{ color: 'var(--text-muted)' }}>...</span>
          ) : (
            <button
              onClick={() => onPageChange(page as number)}
              className={`px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 transition-colors ${
                currentPage === page
                  ? 'text-white hover:opacity-90'
                  : 'border hover:bg-opacity-80'
              }`}
              style={currentPage === page ? {
                backgroundColor: 'var(--astra-dark-blue)'
              } : {
                color: 'var(--text-secondary)',
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-color)'
              }}
              aria-label={`Ir para página ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          )}
        </Fragment>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-2 text-sm font-medium border rounded-md hover:bg-opacity-80 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{
          color: 'var(--text-secondary)',
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-color)'
        }}
        aria-label="Próxima página"
      >
        Próxima
      </button>
    </nav>
  );
}